import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FieldOption } from '../../types/generator-config';
import { createOption } from '../../lib/fieldFactory';

interface OptionsEditorProps {
    options: FieldOption[];
    withIcon?: boolean;
    onChange: (next: FieldOption[]) => void;
}

export function OptionsEditor({ options, withIcon = false, onChange }: OptionsEditorProps) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
    );

    const handleDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = options.findIndex((o) => o.id === active.id);
        const newIdx = options.findIndex((o) => o.id === over.id);
        onChange(arrayMove(options, oldIdx, newIdx));
    };

    const update = (id: string, patch: Partial<FieldOption>) => {
        onChange(options.map((o) => (o.id === id ? { ...o, ...patch } : o)));
    };

    const remove = (id: string) => {
        onChange(options.filter((o) => o.id !== id));
    };

    const add = () => {
        onChange([...options, createOption({ label: `Option ${options.length + 1}` })]);
    };

    return (
        <div className="opts-editor">
            <div className="opts-editor-label">Options</div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                    <ul className="opts-list">
                        {options.map((opt) => (
                            <SortableOption
                                key={opt.id}
                                option={opt}
                                withIcon={withIcon}
                                canRemove={options.length > 1}
                                onChange={(patch) => update(opt.id, patch)}
                                onRemove={() => remove(opt.id)}
                            />
                        ))}
                    </ul>
                </SortableContext>
            </DndContext>

            <button type="button" className="opts-add" onClick={add}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Ajouter une option
            </button>
        </div>
    );
}

interface SortableOptionProps {
    option: FieldOption;
    withIcon: boolean;
    canRemove: boolean;
    onChange: (patch: Partial<FieldOption>) => void;
    onRemove: () => void;
}

function SortableOption({ option, withIcon, canRemove, onChange, onRemove }: SortableOptionProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: option.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <li ref={setNodeRef} style={style} className="opt-row">
            <button
                type="button"
                className="drag-handle"
                aria-label="Réordonner"
                {...attributes}
                {...listeners}
            >
                <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
                    <circle cx="2" cy="2" r="1.2" />
                    <circle cx="8" cy="2" r="1.2" />
                    <circle cx="2" cy="7" r="1.2" />
                    <circle cx="8" cy="7" r="1.2" />
                    <circle cx="2" cy="12" r="1.2" />
                    <circle cx="8" cy="12" r="1.2" />
                </svg>
            </button>

            {withIcon && (
                <input
                    type="text"
                    className="opt-icon"
                    value={option.icon ?? ''}
                    onChange={(e) => onChange({ icon: e.target.value || undefined })}
                    placeholder="Icône"
                    maxLength={4}
                    aria-label="Icône (optionnel)"
                    title="Préfixe court affiché devant l'option (1 à 4 caractères, ex : A, 1, …)"
                />
            )}

            <input
                type="text"
                className="opt-label"
                value={option.label}
                onChange={(e) => onChange({ label: e.target.value })}
                placeholder="Libellé de l'option"
            />

            <button
                type="button"
                className="icon-btn icon-btn-danger"
                onClick={onRemove}
                disabled={!canRemove}
                aria-label="Supprimer cette option"
                title={canRemove ? 'Supprimer' : 'Au moins une option est requise'}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                </svg>
            </button>
        </li>
    );
}
